/**
 * Ticket Lifecycle v2 — FOH Handoff Reasons (Batch 1)
 *
 * Canonical source of truth for the structured reason values + labels
 * that accompany every FOH custody handoff (kick_to_foh / kick_to_boh /
 * reassign_within_foh). Designed so that:
 *
 *   - Manual kebab kicks pick a reason from `MANUAL_KICK_REASONS`.
 *   - Future Pause-dialog kicks pick from `PAUSE_KICK_REASONS`.
 *   - Future Stop-dialog kicks pick from `STOP_KICK_REASONS`.
 *   - "Kick back to tech" picks from `KICK_BACK_REASONS`.
 *   - Implicit advisor takeovers always emit a fixed sentinel.
 *
 * The reason VALUE is stored in `ticket_custody_events.reason` (text)
 * and is the unit of aggregation for analytics. The LABEL is for UI
 * only and may change without breaking historical records.
 *
 * Notable product nuance (preserved here):
 *   - `inspection_complete` and `diagnosis_complete` appear in BOTH the
 *     pause and stop sets, but with **different values** so analytics
 *     can split "tech expects more work after FOH replies" from "tech
 *     considers the authorized scope complete; sell/schedule later".
 *     See `inspection_complete_next_decision` vs
 *     `inspection_complete_sell_or_schedule`.
 *
 * Boundary: this module imports no scheduling / timer / parking /
 * rollover / bay / invariants code. Pure constants + small helpers.
 *
 * Drift discipline:
 *   - `ticket_custody_events.reason` is a free-form `text` column;
 *     adding/removing reasons does NOT require a DB migration.
 *   - The drift detector at
 *     `tests/unit/server/custodyEnumDriftDetector.test.ts` pins the
 *     custody/advisor-action enums; this reason set is a separate
 *     surface and is pinned by its own test in
 *     `tests/unit/lifecycle/custodyHandoffReasons.test.ts`.
 */

// ─── Trigger groups ────────────────────────────────────────────────────

/**
 * Which UI surface emitted the kick. Stored in
 * `ticket_custody_events.metadata.trigger`. Drives analytics
 * segmentation: pause-kicks measure "tech blocked" SLA, stop-kicks
 * measure "FOH close-out" SLA.
 */
export type CustodyHandoffTrigger =
  | 'manual'
  | 'pause_kick'
  | 'stop_kick'
  | 'implicit_takeover';

export interface CustodyHandoffReasonOption {
  /** Canonical value stored in `ticket_custody_events.reason`. */
  value: string;
  /** Short display label for selects, badges, queue rows. */
  label: string;
  /** Optional helper copy for the picker dropdown. */
  description?: string;
  /**
   * Which trigger groups may surface this option. A reason can belong
   * to multiple groups (e.g. `other` belongs to all four).
   */
  triggers: ReadonlyArray<CustodyHandoffTrigger>;
}

// ─── Reason sets ───────────────────────────────────────────────────────

/**
 * Manual kebab Kick-to-Advisor. Used when a tech / manager kicks
 * without specifically pausing or stopping the timer (e.g. they're
 * already off the bay and just need FOH involvement).
 */
export const MANUAL_KICK_REASONS = [
  {
    value: 'advisor_review_needed',
    label: 'Advisor review needed',
    description: 'General handoff — advisor needs to look at the ticket.',
    triggers: ['manual'],
  },
  {
    value: 'customer_follow_up_needed',
    label: 'Customer follow-up needed',
    description: 'Advisor needs to call or message the customer.',
    triggers: ['manual'],
  },
  {
    value: 'estimate_or_quote_needed',
    label: 'Estimate or quote needed',
    description: 'Advisor should build/send the estimate.',
    triggers: ['manual'],
  },
  {
    value: 'parts_or_pricing_needed',
    label: 'Parts or pricing question',
    description: 'Advisor needs to sort parts availability or pricing.',
    triggers: ['manual'],
  },
  {
    value: 'approval_needed',
    label: 'Approval needed',
    description: 'Waiting on customer / manager approval.',
    triggers: ['manual'],
  },
  {
    value: 'other',
    label: 'Other',
    description: 'No category fits — explain in notes if helpful.',
    triggers: ['manual', 'pause_kick', 'stop_kick'],
  },
] as const satisfies ReadonlyArray<CustodyHandoffReasonOption>;

/**
 * Reasons surfaced when the tech is **pausing** the timer and chooses
 * to also kick to FOH. Semantic: "The tech is not done — FOH must act
 * before the tech can continue."
 */
export const PAUSE_KICK_REASONS = [
  {
    value: 'needs_customer_approval',
    label: 'Needs customer approval',
    description: 'Customer must approve before work continues.',
    triggers: ['pause_kick'],
  },
  {
    value: 'needs_estimate',
    label: 'Needs estimate',
    description: 'Advisor must build/send an estimate before work continues.',
    triggers: ['pause_kick'],
  },
  {
    value: 'needs_parts_or_pricing',
    label: 'Needs parts or pricing',
    description: 'Parts availability / pricing must be resolved.',
    triggers: ['pause_kick'],
  },
  {
    value: 'waiting_on_parts',
    label: 'Waiting on parts',
    description: 'Parts already ordered; tech is blocked until arrival.',
    triggers: ['pause_kick'],
  },
  {
    value: 'advisor_question',
    label: 'Advisor question',
    description: 'Tech needs an answer from FOH to proceed.',
    triggers: ['pause_kick'],
  },
  {
    value: 'diagnostic_authorization_needed',
    label: 'Diagnostic authorization needed',
    description: 'Further diagnostic work needs to be authorized.',
    triggers: ['pause_kick'],
  },
  {
    value: 'inspection_complete_next_decision',
    label: 'Inspection complete — awaiting next decision',
    description:
      'Inspection findings are in; more work is expected once FOH/customer decide.',
    triggers: ['pause_kick'],
  },
  {
    value: 'diagnosis_complete_next_decision',
    label: 'Diagnosis complete — awaiting next decision',
    description:
      'Diagnosis is in; more work is expected once FOH/customer decide.',
    triggers: ['pause_kick'],
  },
  {
    value: 'vehicle_blocked',
    label: 'Vehicle blocked',
    description: 'Vehicle cannot be moved / accessed right now.',
    triggers: ['pause_kick'],
  },
  {
    value: 'other',
    label: 'Other',
    description: 'No category fits — explain in notes if helpful.',
    triggers: ['manual', 'pause_kick', 'stop_kick'],
  },
] as const satisfies ReadonlyArray<CustodyHandoffReasonOption>;

/**
 * Reasons surfaced when the tech is **stopping** the timer and chooses
 * to kick to FOH. Semantic: "The tech is done with the currently
 * authorized/scheduled scope — FOH owns the next step."
 */
export const STOP_KICK_REASONS = [
  {
    value: 'inspection_complete_sell_or_schedule',
    label: 'Inspection complete — sell or schedule',
    description:
      'Inspection was the authorized scope; repairs must be sold/scheduled later.',
    triggers: ['stop_kick'],
  },
  {
    value: 'diagnosis_complete_sell_or_schedule',
    label: 'Diagnosis complete — sell or schedule',
    description:
      'Diagnosis was the authorized scope; repairs must be sold/scheduled later.',
    triggers: ['stop_kick'],
  },
  {
    value: 'repair_complete',
    label: 'Repair complete',
    description: 'Authorized repair finished.',
    triggers: ['stop_kick'],
  },
  {
    value: 'ready_for_customer_contact',
    label: 'Ready for customer contact',
    description: 'Advisor should reach out to the customer next.',
    triggers: ['stop_kick'],
  },
  {
    value: 'ready_for_invoice',
    label: 'Ready for invoice',
    description: 'Time to build/send the invoice.',
    triggers: ['stop_kick'],
  },
  {
    value: 'ready_for_pickup',
    label: 'Ready for pickup',
    description: 'Vehicle is ready for the customer to pick up.',
    triggers: ['stop_kick'],
  },
  {
    value: 'qc_needed',
    label: 'QC needed',
    description: 'Quality check before close-out.',
    triggers: ['stop_kick'],
  },
  {
    value: 'other',
    label: 'Other',
    description: 'No category fits — explain in notes if helpful.',
    triggers: ['manual', 'pause_kick', 'stop_kick'],
  },
] as const satisfies ReadonlyArray<CustodyHandoffReasonOption>;

/**
 * Reasons for FOH → BOH (kick back to tech). Stored in
 * `ticket_custody_events.reason` for `kick_to_boh` events.
 */
export const KICK_BACK_REASONS = [
  {
    value: 'more_information_needed',
    label: 'More information needed',
    description: 'Advisor needs the tech to provide more detail.',
    triggers: ['manual'],
  },
  {
    value: 'additional_work_approved',
    label: 'Additional work approved',
    description: 'Customer approved more work — tech can continue.',
    triggers: ['manual'],
  },
  {
    value: 'customer_question_requires_tech',
    label: 'Customer question requires tech',
    description: 'Customer asked something only the tech can answer.',
    triggers: ['manual'],
  },
  {
    value: 'parts_arrived',
    label: 'Parts arrived',
    description: 'Parts are in — tech can resume.',
    triggers: ['manual'],
  },
  {
    value: 'qc_failed',
    label: 'QC failed',
    description: 'Quality check failed — tech needs to revisit.',
    triggers: ['manual'],
  },
  {
    value: 'repair_follow_up_needed',
    label: 'Repair follow-up needed',
    description: 'Further repair work is required.',
    triggers: ['manual'],
  },
  {
    value: 'other',
    label: 'Other',
    description: 'No category fits — explain in notes if helpful.',
    triggers: ['manual'],
  },
] as const satisfies ReadonlyArray<CustodyHandoffReasonOption>;

/**
 * Sentinel value for implicit advisor takeover. Matches the existing
 * `useImplicitAdvisorTakeover` write so the migration to standardized
 * metadata is backwards-compatible with already-logged rows.
 */
export const IMPLICIT_TAKEOVER_REASON = 'implicit_advisor_takeover' as const;

/**
 * Stage E sentinel: the automatic handoff when the LAST live BOH work on a
 * ticket ends (finish_my_work). Never a picker option — nobody chooses it; the
 * server's final-work topology decides it. Trigger group: `stop_kick`.
 */
export const FINAL_STOP_HANDOFF_REASON = 'technician_work_finished' as const;

// ─── Lookup helpers ────────────────────────────────────────────────────

const ALL_REASON_GROUPS: ReadonlyArray<{
  trigger: CustodyHandoffTrigger;
  options: ReadonlyArray<CustodyHandoffReasonOption>;
}> = [
  { trigger: 'manual', options: MANUAL_KICK_REASONS },
  { trigger: 'pause_kick', options: PAUSE_KICK_REASONS },
  { trigger: 'stop_kick', options: STOP_KICK_REASONS },
];

/**
 * Returns the canonical reason options for a given trigger group, plus
 * a kick-back set when `eventKind === 'kick_to_boh'`. Caller uses this
 * to populate the picker dropdown.
 */
export function getCustodyHandoffReasonsFor(
  trigger: CustodyHandoffTrigger,
  eventKind?: 'kick_to_foh' | 'kick_to_boh' | 'reassign_within_foh' | string,
): ReadonlyArray<CustodyHandoffReasonOption> {
  if (eventKind === 'kick_to_boh') return KICK_BACK_REASONS;
  switch (trigger) {
    case 'manual':
      return MANUAL_KICK_REASONS;
    case 'pause_kick':
      return PAUSE_KICK_REASONS;
    case 'stop_kick':
      return STOP_KICK_REASONS;
    case 'implicit_takeover':
      // Implicit takeover never surfaces a picker.
      return [];
  }
}

/**
 * Humanised label for a canonical reason value. Falls back to a
 * cleaned-up version of the raw value (snake_case → title case) when
 * the value is unknown — so older custody events (logged before this
 * Batch) still render something readable.
 */
export function getCustodyHandoffReasonLabel(
  value: string | null | undefined,
): string {
  if (!value) return 'No reason provided';
  if (value === IMPLICIT_TAKEOVER_REASON) return 'Implicit takeover';
  if (value === FINAL_STOP_HANDOFF_REASON) return 'Technician work finished';
  for (const group of ALL_REASON_GROUPS) {
    const hit = group.options.find((o) => o.value === value);
    if (hit) return hit.label;
  }
  for (const kb of KICK_BACK_REASONS) {
    if (kb.value === value) return kb.label;
  }
  // Unknown — best-effort prettify.
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function isPauseKickReason(value: string | null | undefined): boolean {
  if (!value) return false;
  return PAUSE_KICK_REASONS.some((o) => o.value === value);
}

export function isStopKickReason(value: string | null | undefined): boolean {
  if (!value) return false;
  return STOP_KICK_REASONS.some((o) => o.value === value);
}

export function isManualKickReason(value: string | null | undefined): boolean {
  if (!value) return false;
  return MANUAL_KICK_REASONS.some((o) => o.value === value);
}

export function isKickBackReason(value: string | null | undefined): boolean {
  if (!value) return false;
  return KICK_BACK_REASONS.some((o) => o.value === value);
}

/**
 * Normalize a reason value so it is safe to persist. Trims, lower-cases
 * structural noise, and returns `null` for empty / placeholder input.
 * Unknown values are returned unchanged (free-form `reason` column
 * accepts any text — analytics will treat them as `other`).
 */
export function normalizeCustodyHandoffReason(
  value: string | null | undefined,
): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed;
}

// ─── Standardized metadata contract ────────────────────────────────────

/**
 * Stored at `ticket_custody_events.metadata`. Keys are snake_case per
 * the DB convention. Every field except `trigger` and `trigger_reason`
 * is optional so callers can adopt the contract incrementally and so
 * older event rows (which used a flatter shape) still round-trip.
 */
export interface CustodyHandoffMetadata {
  trigger: CustodyHandoffTrigger;
  trigger_reason: string;
  /** Convenience label snapshot for ops triage / log inspection. */
  trigger_reason_label?: string;
  /** Timer state at the moment of the kick, when known. */
  timer_status_at?: 'running' | 'paused' | 'stopped' | null;
  /** Mirrors `tickets.pause_reason` when known. */
  pause_reason?: string | null;
  /** High-level provenance string. Mirrors existing `source` keys. */
  source?: string;
  /** Specific component / surface that emitted the kick. */
  source_component?: string;
  /** Optional free-form note from the user. */
  notes?: string | null;
  /**
   * Allow caller-supplied extensions without losing typing on the
   * common keys. Keep the shape additive; no breaking renames.
   */
  [extra: string]: unknown;
}

export interface BuildCustodyHandoffMetadataArgs {
  trigger: CustodyHandoffTrigger;
  reason: string;
  reasonLabel?: string;
  timerStatusAt?: 'running' | 'paused' | 'stopped' | null;
  pauseReason?: string | null;
  source?: string;
  sourceComponent?: string;
  notes?: string | null;
  /** Caller-provided extras merged AFTER the canonical keys. */
  extra?: Record<string, unknown>;
}

/**
 * Builds the standardized `ticket_custody_events.metadata` payload.
 *
 * Defaults to deriving `source` from `trigger` (e.g.
 * `'custody_transition_manual'`) and `trigger_reason_label` from the
 * reason value via `getCustodyHandoffReasonLabel`. Either may be
 * overridden via the args.
 */
export function buildCustodyHandoffMetadata(
  args: BuildCustodyHandoffMetadataArgs,
): CustodyHandoffMetadata {
  const reason = normalizeCustodyHandoffReason(args.reason) ?? 'other';
  const reasonLabel =
    args.reasonLabel ?? getCustodyHandoffReasonLabel(reason);
  const source = args.source ?? `custody_transition_${args.trigger}`;
  const base: CustodyHandoffMetadata = {
    trigger: args.trigger,
    trigger_reason: reason,
    trigger_reason_label: reasonLabel,
    source,
  };
  if (args.sourceComponent !== undefined)
    base.source_component = args.sourceComponent;
  if (args.timerStatusAt !== undefined)
    base.timer_status_at = args.timerStatusAt;
  if (args.pauseReason !== undefined) base.pause_reason = args.pauseReason;
  if (args.notes !== undefined) base.notes = args.notes;
  if (args.extra) {
    for (const [k, v] of Object.entries(args.extra)) {
      // Don't let extras silently overwrite the structural keys.
      if (k in base && base[k as keyof CustodyHandoffMetadata] !== undefined)
        continue;
      base[k] = v;
    }
  }
  return base;
}

/**
 * Best-effort extractor for the standardized metadata shape from a raw
 * `ticket_custody_events.metadata` jsonb blob. Returns `null` when the
 * blob is empty / non-conforming so callers can render "No reason
 * provided" gracefully for legacy rows.
 */
export function readCustodyHandoffMetadata(
  raw: unknown,
): CustodyHandoffMetadata | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const trigger = obj.trigger;
  const triggerReason = obj.trigger_reason;
  if (
    typeof trigger !== 'string' ||
    typeof triggerReason !== 'string' ||
    !triggerReason
  ) {
    return null;
  }
  const known: ReadonlyArray<CustodyHandoffTrigger> = [
    'manual',
    'pause_kick',
    'stop_kick',
    'implicit_takeover',
  ];
  if (!known.includes(trigger as CustodyHandoffTrigger)) return null;
  return obj as CustodyHandoffMetadata;
}
